import { formatPercent } from '../../demo/format'
import type { Ad, AnalyticsLens, ProductLensRow } from '../../demo/types'

export interface ProductAdMetrics {
  spend: number
  impressions: number
  orders: number
  reach: number
}

function allocateInteger(total: number, weights: number[]): number[] {
  const weightTotal = weights.reduce((sum, value) => sum + value, 0)
  if (weightTotal === 0) return weights.map(() => 0)
  const raw = weights.map((weight) => total * weight / weightTotal)
  const result = raw.map(Math.floor)
  const order = raw
    .map((value, index) => ({ index, remainder: value - (result[index] ?? 0) }))
    .sort((a, b) => b.remainder - a.remainder)
  const remainder = total - result.reduce((sum, value) => sum + value, 0)
  for (let index = 0; index < remainder; index += 1) {
    const target = order[index % order.length]?.index
    if (target !== undefined) result[target] = (result[target] ?? 0) + 1
  }
  return result
}

function spread(total: number, values: number[]): number[] {
  if (values.reduce((sum, value) => sum + value, 0) === total) return values
  return allocateInteger(total, values)
}

export function spreadProductRow(row: ProductLensRow, ads: Ad[], lens: AnalyticsLens): ProductAdMetrics[] {
  const originalSpend = ads.reduce((sum, ad) => sum + ad.spend, 0)
  const factor = originalSpend > 0 ? row.spend / originalSpend : 0
  const target = {
    spend: row.spend,
    impressions: lens === 'awareness'
      ? row.impressions
      : Math.round(factor * ads.reduce((sum, ad) => sum + ad.impressions, 0)),
    orders: lens === 'sales'
      ? row.units
      : Math.round(factor * ads.reduce((sum, ad) => sum + ad.orders, 0)),
    reach: lens === 'awareness'
      ? row.reach
      : Math.round(factor * ads.reduce((sum, ad) => sum + ad.reach, 0)),
  }
  const spend = spread(target.spend, ads.map((ad) => ad.spend))
  const impressions = spread(target.impressions, ads.map((ad) => ad.impressions))
  const orders = spread(target.orders, ads.map((ad) => ad.orders))
  const reach = spread(target.reach, ads.map((ad) => ad.reach))
  return ads.map((_, index) => ({
    spend: spend[index] ?? 0,
    impressions: impressions[index] ?? 0,
    orders: orders[index] ?? 0,
    reach: reach[index] ?? 0,
  }))
}

export function formatRatio(numerator: number, denominator: number): string {
  return denominator === 0 ? '—' : formatPercent(numerator / denominator)
}

export function formatDelta(value: number | null): string {
  if (value === null) return '—'
  return `${value >= 0 ? '+' : '−'}${Math.abs(value * 100).toFixed(0)}%`
}
